import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function Calendar({ holidays = [], leaves = [], onDateClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateKey = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const isDateInRange = (dateStr, startDate, endDate) => {
    const date = new Date(dateStr);
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Normalize to start of day for comparison
    date.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return date >= start && date <= end;
  };

  // Create a map of dates to their events
  const dateEvents = useMemo(() => {
    const events = {};
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Add holidays
    holidays.forEach(holiday => {
      const holidayDate = new Date(holiday.date);
      if (holidayDate.getFullYear() === year && holidayDate.getMonth() === month) {
        const key = formatDateKey(year, month, holidayDate.getDate());
        if (!events[key]) events[key] = { holidays: [], leaves: [] };
        events[key].holidays.push(holiday);
      }
    });

    // Add leaves
    leaves.forEach(leave => {
      const days = daysInMonth(currentDate);
      for (let day = 1; day <= days; day++) {
        const dateKey = formatDateKey(year, month, day);
        if (isDateInRange(dateKey, leave.start_date, leave.end_date)) {
          if (!events[dateKey]) events[dateKey] = { holidays: [], leaves: [] };
          events[dateKey].leaves.push(leave);
        }
      }
    });

    return events;
  }, [holidays, leaves, currentDate]);

  const renderCalendar = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square p-1">
          <div className="h-full"></div>
        </div>
      );
    }

    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const dateKey = formatDateKey(year, month, day);
      const events = dateEvents[dateKey] || { holidays: [], leaves: [] };
      const isToday =
        today.getDate() === day &&
        today.getMonth() === month &&
        today.getFullYear() === year;

      const hasHoliday = events.holidays.length > 0;
      const hasLeave = events.leaves.length > 0;
      const approvedLeaves = events.leaves.filter(l => l.status === 'approved');
      const pendingLeaves = events.leaves.filter(l => l.status === 'pending');

      days.push(
        <div
          key={day}
          className={`aspect-square p-1 ${onDateClick ? 'cursor-pointer' : ''}`}
          onClick={() => onDateClick && onDateClick(dateKey, events)}
        >
          <div
            className={`h-full rounded-lg p-2 transition-all ${
              isToday
                ? 'bg-primary text-white font-bold'
                : hasHoliday
                ? 'bg-purple-100 hover:bg-purple-200'
                : hasLeave
                ? 'bg-blue-50 hover:bg-blue-100'
                : 'hover:bg-slate-50'
            }`}
          >
            <div className="text-sm font-medium mb-1">{day}</div>
            <div className="space-y-0.5">
              {hasHoliday && (
                <div
                  className="text-xs bg-purple-500 text-white px-1 py-0.5 rounded truncate"
                  title={events.holidays.map(h => h.name).join(', ')}
                >
                  {events.holidays.length > 1
                    ? `${events.holidays.length} holidays`
                    : events.holidays[0].name.substring(0, 10)}
                </div>
              )}
              {approvedLeaves.length > 0 && (
                <div
                  className="text-xs bg-green-500 text-white px-1 py-0.5 rounded truncate"
                  title={approvedLeaves.map(l => `${l.user_name} - ${l.leave_type}`).join(', ')}
                >
                  {approvedLeaves.length > 1
                    ? `${approvedLeaves.length} leaves`
                    : approvedLeaves[0].user_name}
                </div>
              )}
              {pendingLeaves.length > 0 && (
                <div
                  className="text-xs bg-orange-400 text-white px-1 py-0.5 rounded truncate"
                  title={pendingLeaves.map(l => `${l.user_name} - ${l.leave_type} (pending)`).join(', ')}
                >
                  {pendingLeaves.length > 1
                    ? `${pendingLeaves.length} pending`
                    : `${pendingLeaves[0].user_name} (pending)`}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-foreground-DEFAULT rounded-lg transition-all"
          >
            Today
          </button>
          <button
            onClick={previousMonth}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-primary"></div>
          <span className="text-foreground-muted">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-500"></div>
          <span className="text-foreground-muted">Holiday</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-foreground-muted">Approved Leave</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-400"></div>
          <span className="text-foreground-muted">Pending Leave</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="aspect-square flex items-center justify-center text-sm font-semibold text-foreground-muted p-1"
          >
            {day}
          </div>
        ))}
        {/* Calendar days */}
        {renderCalendar()}
      </div>
    </div>
  );
}

export default Calendar;
